import type {
  EvaluationContext,
  JsonValue,
  Provider,
  ResolutionDetails,
} from "@openfeature/web-sdk";
import { posthog } from "posthog-js";

/**
 * Thin OpenFeature provider over posthog-js.
 *
 * There is no official `@openfeature/posthog-provider` for the browser
 * (verified on npm: 404 for the web SDK; only third-party node providers exist),
 * so we wrap the PostHog client ourselves. This is the ONLY flags module that
 * touches the PostHog SDK.
 *
 * Single-instance guarantee: posthog-js exposes a module-level singleton, so the
 * `posthog` imported here is the very same instance that `@repo/analytics`
 * initializes via `analytics.init(...)`. We deliberately do NOT call
 * `posthog.init` here — the analytics package owns initialization. The provider
 * only reads flags and reloads them when the evaluation context changes.
 */

const PROVIDER_NAME = "posthog-web";

/** Coerce a PostHog feature-flag result into a boolean. */
function asBoolean(value: string | boolean | undefined): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

export class PostHogWebProvider implements Provider {
  readonly runsOn = "client" as const;
  readonly metadata = { name: PROVIDER_NAME } as const;

  // We don't use OpenFeature's event emitter; React hooks subscribe directly to
  // PostHog's `onFeatureFlags` reload via `subscribe()` below.
  readonly events = undefined;
  private readonly listeners = new Set<() => void>();

  async initialize(context?: EvaluationContext): Promise<void> {
    // posthog-js is initialized by @repo/analytics. If a targetingKey is
    // provided, align PostHog's distinct id so flag targeting matches identity.
    if (context?.targetingKey && typeof context.targetingKey === "string") {
      posthog.identify(context.targetingKey);
    }
    posthog.onFeatureFlags(() => {
      for (const l of this.listeners) l();
    });
  }

  async onContextChange(
    _oldContext: EvaluationContext,
    newContext: EvaluationContext,
  ): Promise<void> {
    if (newContext.targetingKey && typeof newContext.targetingKey === "string") {
      posthog.identify(newContext.targetingKey);
    }
    // Force a fresh evaluation for the new context.
    posthog.reloadFeatureFlags();
  }

  /** Internal: lets the adapter subscribe to PostHog flag reloads. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  resolveBooleanEvaluation(flagKey: string, defaultValue: boolean): ResolutionDetails<boolean> {
    const enabled = asBoolean(posthog.isFeatureEnabled(flagKey));
    if (enabled === undefined) {
      return { value: defaultValue, reason: "DEFAULT" };
    }
    return { value: enabled, reason: "TARGETING_MATCH" };
  }

  resolveStringEvaluation(flagKey: string, defaultValue: string): ResolutionDetails<string> {
    const value = posthog.getFeatureFlag(flagKey);
    if (typeof value === "string") {
      return { value, reason: "TARGETING_MATCH" };
    }
    return { value: defaultValue, reason: "DEFAULT" };
  }

  resolveNumberEvaluation(flagKey: string, defaultValue: number): ResolutionDetails<number> {
    // PostHog multivariate flags are strings; coerce numeric variants.
    const value = posthog.getFeatureFlag(flagKey);
    const num = typeof value === "string" ? Number(value) : Number.NaN;
    if (Number.isFinite(num)) {
      return { value: num, reason: "TARGETING_MATCH" };
    }
    return { value: defaultValue, reason: "DEFAULT" };
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
  ): ResolutionDetails<T> {
    // PostHog flag *payloads* are arbitrary JSON.
    const payload = posthog.getFeatureFlagPayload(flagKey) as T | undefined;
    if (payload !== undefined && payload !== null) {
      return { value: payload, reason: "TARGETING_MATCH" };
    }
    return { value: defaultValue, reason: "DEFAULT" };
  }
}
